/**
 * ★ A SUBMIT REFUSED FOR STALE TERMS IS A DETOUR, NOT A DEAD END.
 *
 * `submitArticle` can be refused 409 `TERMS_ACCEPTANCE_STALE`: "the author's terms acceptance is
 * stale" (docs/design/07-api/authors-api.yaml:1445). Until 2026-09-23 the editor put that through
 * `describeProblem` like any other refusal - which has no sentence for the code, so the author read
 * the server's `detail` - and nothing on the screen said where acceptance happens. It happens on
 * the terms interstitial in one click, so this names the reason and links there WITH THE WAY BACK:
 * `termsPathFor` carries this draft's own address as `next` (features/auth/navigation.ts), and the
 * live `/terms` route sends "Continue" there once the acceptance lands
 * (routes/support/session-routes.tsx, `LiveTermsInterstitial`).
 *
 * THE DRAFT IS SAVED, AND THE NOTICE SAYS SO: Submit saves first and asks for the transition only
 * once the save has landed (`../EditorRoute.tsx`, "SUBMIT SAVES FIRST, ALWAYS"), so a refused
 * transition never cost the author a word.
 *
 * MONEY IS NAMED, because a demand to accept something reads as a threat to what was earned: "a
 * stale acceptance blocks submitting, resubmitting and publishing" and never a payout run crediting
 * the author (docs/design/04-schema.md:1770-1772). The last sentence is the terms screen's own,
 * word for word (`blockedNoticeBody`, features/support/strings.ts), so both screens say it one way.
 *
 * KEYED ON THE CODE, NOT ON THE TRANSITION. The contract declares this refusal on `submitArticle`
 * alone, while the ruling just quoted blocks resubmitting too; a resubmit that answers it lands
 * here the same way.
 */
import { Alert, Stack, Text } from '@mantine/core';
import { termsPathFor } from '../../../features/auth/navigation';
import { InAppAnchor } from '../../../features/shell/InAppAnchor';
import { Trans, useTranslation } from '../../../i18n';
import { usePieceContext } from './piece-context';

export interface TermsStaleNoticeProps {
  /** Where the terms interstitial sends the author back to: this draft's editor. */
  readonly returnTo: string;
  /** Dismisses the notice, as the editor's other refusals are dismissed. */
  readonly onClose: () => void;
}

export function TermsStaleNotice({ returnTo, onClose }: TermsStaleNoticeProps) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  return (
    <Alert
      variant="light"
      role="alert"
      title={t('editor.termsStale.title')}
      withCloseButton
      closeButtonLabel={t('common.dismiss')}
      onClose={onClose}
    >
      <Stack gap="xs">
        <Text size="sm">{t('editor.termsStale.body', { context })}</Text>
        <Text size="sm">
          {/* The link's words are the catalogue's, put in its place by `Trans`. */}
          <Trans
            t={t}
            i18nKey="editor.termsStale.link"
            components={{
              anchor: (
                <InAppAnchor href={termsPathFor(returnTo)} size="sm">
                  {null}
                </InAppAnchor>
              ),
            }}
          />
        </Text>
      </Stack>
    </Alert>
  );
}
